import React from "react";
import { Nav, NavItem, NavLink, TabContent, TabPane } from "reactstrap";
import { NavLink as RouterNavLink, Route, Switch, Redirect, useLocation, matchPath } from "react-router-dom";

import { getParamsAttachedRoute } from "../../helpers/routeHelpers";

/**
 * Tabs whose active pane is derived from the URL.
 *
 * It previously held the active tab in useState, seeded from `defaultActive`
 * and updated only by a NavLink onClick. That meant the URL and the visible
 * pane could disagree: the <Route> would render the correct TabPane, but
 * `TabContent activeTab` still pointed at the default tab, so reactstrap kept
 * the rendered pane at `display: none`.
 *
 * The user-visible symptom was that deep-linking or refreshing on any tab
 * other than the default produced a completely blank panel — the tab looked
 * selected, and nothing was under it. Clicking through from the default tab
 * worked, which is why it survived: nobody reloads mid-flow while building a
 * feature. Confirmed on /mcap/wellbeing before the fix.
 *
 * Deriving from `useLocation` makes the URL the single source of truth, so a
 * refresh, a pasted link, and a click all land in the same state. The onClick
 * is gone because it is redundant — the NavLink already navigates, and the
 * navigation is what recomputes the active tab.
 */
const RoutingTabs = ({ config = null, isTabs = true, isPills = false, isFill = false, redirectionFromRoute = "" }) => {
    const location = useLocation();

    const resolvedPath = (item) => getParamsAttachedRoute(item.path, config?.param);

    // matchPath rather than a string compare: tab paths may carry params
    // (CustomerManagement's tabs are `/customer-details/:id/mood`), so the
    // literal path never equals the current pathname there.
    const routedTab = config.tabsConfig.find((item) =>
        matchPath(location.pathname, { path: resolvedPath(item) })
    );

    // Falls back to the default tab at the parent route, which is the moment
    // getRedirect() below sends the user onward to that tab's own URL.
    const activeTab = routedTab || config.tabsConfig.find((item) => item.defaultActive);

    const getRedirect = () => {
        if (!activeTab) return null;

        return (
            <Redirect
                exact
                from={getParamsAttachedRoute(redirectionFromRoute, config?.param)}
                to={resolvedPath(activeTab)}
            />
        );
    };

    return (
        <div>
            <Nav tabs={isTabs} pills={isPills} fill={isFill}>
                {config.tabsConfig.map((item) => (
                    <NavItem key={item.identifier}>
                        <NavLink exact tag={RouterNavLink} to={resolvedPath(item)}>
                            {item.title}
                        </NavLink>
                    </NavItem>
                ))}
            </Nav>

            <TabContent activeTab={activeTab?.id}>
                <Switch>
                    {getRedirect()}
                    {/* The pattern (`/customer-details/:id/mood`), not the resolved
                        path: a route whose path has the id already substituted
                        carries no :id, so useParams() inside the tab returned {}
                        and every Customer Details tab requested userId=undefined. */}
                    {config.tabsConfig.map((item) => (
                        <Route key={item.identifier} path={item.path}>
                            <TabPane tabId={item.id}>{item.component}</TabPane>
                        </Route>
                    ))}
                    {/* A path no tab claims — e.g. an old link to a tab that is
                        not offered for this record — goes to the default tab
                        rather than to an empty panel. */}
                    {activeTab ? <Redirect to={resolvedPath(activeTab)} /> : null}
                </Switch>
            </TabContent>
        </div>
    );
};

export default RoutingTabs;
